import { useEffect, useRef, useState } from 'react';
import {
  DndContext, DragOverlay, PointerSensor, closestCenter, useDroppable, useSensor, useSensors,
} from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api } from '../api.js';
import { Spinner, ErrorNote } from '../components.jsx';
import AppCard from './AppCard.jsx';
import AppEditModal from './AppEditModal.jsx';

function SortableAppCard({ app, editMode, onEdit, onToggleHidden }) {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id: app.key,
    data: { type: 'app' },
    disabled: !editMode,
  });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.3 : 1 };
  return (
    <AppCard
      app={app}
      editMode={editMode}
      cardRef={setNodeRef}
      style={style}
      dragHandleProps={{ ...attributes, ...listeners }}
      onEdit={onEdit}
      onToggleHidden={onToggleHidden}
    />
  );
}

function BucketColumn({ bucket, sortableId, appKeys, appsByKey, editMode, onRename, onDelete, onEditApp, onToggleHidden }) {
  const sortable = useSortable({ id: sortableId, data: { type: 'bucket' }, disabled: !editMode || !sortableId });
  const droppable = useDroppable({ id: `col:${bucket.id}`, data: { type: 'column' } });
  const isFixed = !sortableId;

  const style = isFixed ? {} : { transform: CSS.Transform.toString(sortable.transform), transition: sortable.transition };

  return (
    <div ref={isFixed ? undefined : sortable.setNodeRef} style={style} className="flex w-72 shrink-0 flex-col gap-2">
      <div className="flex items-center gap-1.5 px-1">
        {editMode && !isFixed && (
          <span {...sortable.attributes} {...sortable.listeners} className="cursor-grab touch-none text-steel/60 active:cursor-grabbing" title="Drag to reorder">⠿</span>
        )}
        <h3 className="flex-1 truncate text-sm font-bold uppercase tracking-wide text-steel">{bucket.name}</h3>
        {editMode && !isFixed && (
          <>
            <button onClick={() => onRename(bucket)} className="rounded p-1 text-steel hover:bg-steel/10" title="Rename">✎</button>
            <button onClick={() => onDelete(bucket)} className="rounded p-1 text-steel hover:bg-ascred/10 hover:text-ascred" title="Delete bucket">🗑</button>
          </>
        )}
      </div>
      <div ref={droppable.setNodeRef} className={`flex min-h-[64px] flex-col gap-2 rounded-lg p-1 transition ${droppable.isOver ? 'bg-steel/10' : ''}`}>
        <SortableContext items={appKeys} strategy={verticalListSortingStrategy}>
          {appKeys.map((key) => appsByKey[key] && (
            <SortableAppCard key={key} app={appsByKey[key]} editMode={editMode} onEdit={onEditApp} onToggleHidden={onToggleHidden} />
          ))}
        </SortableContext>
        {appKeys.length === 0 && (
          <div className="rounded-lg border-2 border-dashed border-steel/20 p-4 text-center text-xs text-steel/50">
            {editMode ? 'Drag apps here' : 'No apps'}
          </div>
        )}
      </div>
    </div>
  );
}

// apiBase: '/api/home' for the logged-in user, or `/api/manager/users/:sub` for a manager editing
// someone else -- all mutation routes (buckets/apps) hang off this. loadUrl is where the merged
// home view is actually read from: it's apiBase itself for '/api/home', but the manager route
// nests the read under `${apiBase}/home` (see server/routes/manager.js).
export default function HomeBoard({ apiBase, loadUrl, editMode }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [columns, setColumns] = useState(null); // { 'unsorted' | bucketIdStr: [appKey,...] }
  const [bucketOrder, setBucketOrder] = useState([]); // [bucketIdStr,...]
  const [appsByKey, setAppsByKey] = useState({});
  const [editingApp, setEditingApp] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [newBucketName, setNewBucketName] = useState('');
  const dragStartCol = useRef(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const load = () => {
    setError(null);
    api
      .get(loadUrl)
      .then((d) => {
        setData(d);
        const byKey = {};
        d.apps.forEach((a) => { byKey[a.key] = a; });
        setAppsByKey(byKey);

        const cols = { unsorted: [] };
        d.buckets.forEach((b) => { cols[String(b.id)] = []; });
        d.apps
          .filter((a) => !a.hidden)
          .slice()
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .forEach((a) => {
            const colId = a.bucketId != null && cols[String(a.bucketId)] ? String(a.bucketId) : 'unsorted';
            cols[colId].push(a.key);
          });
        setColumns(cols);
        setBucketOrder(d.buckets.map((b) => String(b.id)));
      })
      .catch((e) => setError(e.message));
  };

  useEffect(load, [apiBase, loadUrl]);

  if (error) return <ErrorNote>Couldn't load your homepage: {error}</ErrorNote>;
  if (!data || !columns) return <Spinner label="Loading your apps…" />;

  const hiddenApps = data.apps.filter((a) => a.hidden);

  function findColumnOfApp(key) {
    return Object.keys(columns).find((colId) => columns[colId].includes(key));
  }

  async function persistColumn(colId, items) {
    const bucketId = colId === 'unsorted' ? null : Number(colId);
    try {
      await Promise.all(
        items.map((key, i) => {
          const app = appsByKey[key];
          if (!app) return null;
          return api.patch(`${apiBase}/apps`, {
            pocketidClientId: app.source === 'pocketid' ? app.pocketidClientId : undefined,
            dbId: app.source === 'custom' ? app.dbId : undefined,
            bucketId,
            sortOrder: i,
          });
        })
      );
    } catch (err) {
      console.error('Failed to save layout:', err);
      setError('Some changes may not have saved — try again.');
    }
  }

  function handleDragStart(event) {
    setActiveId(event.active.id);
    if (event.active.data.current?.type === 'app') dragStartCol.current = findColumnOfApp(event.active.id);
  }

  function handleDragOver(event) {
    const { active, over } = event;
    if (!over || active.data.current?.type !== 'app') return;
    const activeKey = active.id;
    const fromCol = findColumnOfApp(activeKey);
    const overIsColumn = String(over.id).startsWith('col:');
    const toCol = overIsColumn ? over.id.slice(4) : findColumnOfApp(over.id);
    if (!fromCol || !toCol || fromCol === toCol) return;

    setColumns((prev) => {
      const fromItems = prev[fromCol].filter((k) => k !== activeKey);
      const toItems = [...prev[toCol]];
      const overIndex = overIsColumn ? toItems.length : toItems.indexOf(over.id);
      toItems.splice(overIndex >= 0 ? overIndex : toItems.length, 0, activeKey);
      return { ...prev, [fromCol]: fromItems, [toCol]: toItems };
    });
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;

    if (active.data.current?.type === 'bucket') {
      const oldIndex = bucketOrder.indexOf(active.id);
      const newIndex = bucketOrder.indexOf(over.id);
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;
      const next = arrayMove(bucketOrder, oldIndex, newIndex);
      setBucketOrder(next);
      api.patch(`${apiBase}/buckets/reorder`, { order: next.map(Number) }).catch((e) => setError(e.message));
      return;
    }

    const activeKey = active.id;
    const finalCol = findColumnOfApp(activeKey);
    const startCol = dragStartCol.current;
    dragStartCol.current = null;
    if (!finalCol) return;

    setColumns((prev) => {
      const items = [...prev[finalCol]];
      const oldIndex = items.indexOf(activeKey);
      const overIsColumn = String(over.id).startsWith('col:');
      const overIndex = overIsColumn ? items.length - 1 : items.indexOf(over.id);
      const reordered = oldIndex !== -1 && overIndex !== -1 && oldIndex !== overIndex ? arrayMove(items, oldIndex, overIndex) : items;
      persistColumn(finalCol, reordered);
      if (startCol && startCol !== finalCol) persistColumn(startCol, prev[startCol]);
      return { ...prev, [finalCol]: reordered };
    });
  }

  const addBucket = async (e) => {
    e.preventDefault();
    if (!newBucketName.trim()) return;
    try {
      await api.post(`${apiBase}/buckets`, { name: newBucketName.trim() });
      setNewBucketName('');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const renameBucket = async (bucket) => {
    const name = window.prompt('Rename bucket', bucket.name);
    if (!name || !name.trim() || name === bucket.name) return;
    try {
      await api.patch(`${apiBase}/buckets/${bucket.id}`, { name: name.trim() });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteBucket = async (bucket) => {
    if (!window.confirm(`Delete "${bucket.name}"? Apps inside move to Unsorted.`)) return;
    try {
      await api.del(`${apiBase}/buckets/${bucket.id}`);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleHidden = async (app) => {
    try {
      await api.patch(`${apiBase}/apps`, {
        pocketidClientId: app.source === 'pocketid' ? app.pocketidClientId : undefined,
        dbId: app.source === 'custom' ? app.dbId : undefined,
        hidden: !app.hidden,
      });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const saveApp = async (fields) => {
    if (editingApp.isNew) {
      await api.post(`${apiBase}/apps`, { ...fields, bucketId: null });
    } else {
      await api.patch(`${apiBase}/apps`, {
        pocketidClientId: editingApp.source === 'pocketid' ? editingApp.pocketidClientId : undefined,
        dbId: editingApp.source === 'custom' ? editingApp.dbId : undefined,
        ...fields,
      });
    }
    setEditingApp(null);
    load();
  };

  const deleteApp = async () => {
    if (!window.confirm(`Remove "${editingApp.name}"?`)) return;
    try {
      await api.del(`${apiBase}/apps/${editingApp.dbId}`);
      setEditingApp(null);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const bucketList = bucketOrder.map((id) => data.buckets.find((b) => String(b.id) === id)).filter(Boolean);
  const activeApp = activeId ? appsByKey[activeId] : null;

  // Read-only view (mobile, or desktop before "Edit layout" is toggled on):
  // the horizontally-scrolling fixed-width Kanban board below is built for
  // drag-and-drop editing on desktop. Forcing that same layout onto a phone
  // meant every bucket became its own 288px-wide side-scrolling column --
  // this is a plain stacked, full-width, wrapping grid instead.
  if (!editMode) {
    const sections = [...bucketList.map((b) => ({ id: String(b.id), name: b.name })), { id: 'unsorted', name: 'Unsorted' }]
      .map((section) => ({ ...section, keys: columns[section.id] || [] }))
      .filter((section) => section.keys.length > 0);

    return (
      <div className="space-y-6">
        {data.pocketidError && (
          <ErrorNote>Couldn't reach PocketID — showing whatever apps you've already customised. ({data.pocketidError})</ErrorNote>
        )}
        {sections.length === 0 && <p className="text-sm text-steel">No apps to show yet.</p>}
        {sections.map((section) => (
          <div key={section.id}>
            <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-steel">{section.name}</h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {section.keys.map((key) => appsByKey[key] && (
                <AppCard key={key} app={appsByKey[key]} editMode={false} dragHandleProps={{}} onEdit={() => {}} onToggleHidden={() => {}} />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {data.pocketidError && <ErrorNote>Couldn't reach PocketID — showing whatever apps you've already customised. ({data.pocketidError})</ErrorNote>}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
        <SortableContext items={bucketOrder} strategy={horizontalListSortingStrategy}>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {bucketList.map((bucket) => (
              <BucketColumn
                key={bucket.id}
                bucket={bucket}
                sortableId={String(bucket.id)}
                appKeys={columns[String(bucket.id)] || []}
                appsByKey={appsByKey}
                editMode={editMode}
                onRename={renameBucket}
                onDelete={deleteBucket}
                onEditApp={setEditingApp}
                onToggleHidden={toggleHidden}
              />
            ))}
            {/* Unsorted sits last (right-hand side) and disappears once empty in view mode --
                it still needs to render (even empty) in edit mode so it stays a valid drop target. */}
            {((columns.unsorted || []).length > 0 || editMode) && (
              <BucketColumn
                bucket={{ id: 'unsorted', name: 'Unsorted' }}
                sortableId={null}
                appKeys={columns.unsorted || []}
                appsByKey={appsByKey}
                editMode={editMode}
                onEditApp={setEditingApp}
                onToggleHidden={toggleHidden}
              />
            )}
            {editMode && (
              <form onSubmit={addBucket} className="flex w-56 shrink-0 flex-col gap-2">
                <input
                  className="input"
                  placeholder="New bucket name"
                  value={newBucketName}
                  onChange={(e) => setNewBucketName(e.target.value)}
                />
                <button type="submit" className="btn-ghost">+ Add bucket</button>
              </form>
            )}
          </div>
        </SortableContext>
        <DragOverlay>
          {activeApp && <AppCard app={activeApp} editMode={editMode} dragHandleProps={{}} onEdit={() => {}} onToggleHidden={() => {}} />}
        </DragOverlay>
      </DndContext>

      {editMode && (
        <button className="btn-ghost" onClick={() => setEditingApp({ isNew: true, source: 'custom', name: '', description: '', launchUrl: '', iconPath: '' })}>
          + Add a custom app
        </button>
      )}

      {editMode && hiddenApps.length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-steel">Hidden apps</h3>
          <div className="flex flex-wrap gap-2">
            {hiddenApps.map((app) => (
              <button key={app.key} onClick={() => toggleHidden(app)} className="badge bg-steel/10 text-steel hover:bg-steel/20" title="Unhide">
                {app.name} · unhide
              </button>
            ))}
          </div>
        </div>
      )}

      {editingApp && (
        <AppEditModal
          app={editingApp}
          onClose={() => setEditingApp(null)}
          onSave={saveApp}
          onDelete={deleteApp}
        />
      )}
    </div>
  );
}
